import React from 'react';
import { useIntl } from 'react-intl';
import Select, { components } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import PropTypes from 'prop-types';

const AddressSelectWidget = ({ disabled, label, onChange, onCreateOption, options, required, value }) => {
  const intl = useIntl();

  const selectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: '37px',
      height: '37px',
      borderRadius: '6px',
      borderColor: state.isFocused ? '#5882C1' : '#ced4da',
      boxShadow: state.isFocused ? '0 0 0 1px rgba(88, 130, 193, 0.1)' : 'none',
      backgroundColor: disabled ? '#efefef' : 'white',
      fontFamily: "'Open Sans', sans-serif",
      fontSize: '14px',
      fontWeight: 'normal',
      '&:hover': {
        borderColor: state.isFocused ? '#5882C1' : '#b0b0b0',
      },
    }),
    valueContainer: base => ({
      ...base,
      padding: '0 12px',
      height: '100%',
    }),
    input: base => ({
      ...base,
      margin: 0,
      padding: 0,
      '& input': {
        height: '100% !important',
      },
    }),
    option: (base, state) => ({
      ...base,
      fontSize: '14px',
      fontWeight: 'normal',
      fontFamily: "'Open Sans', sans-serif",
      backgroundColor: state.isSelected ? '#5882C1' : state.isFocused ? '#f0f4f9' : 'transparent',
      color: state.isSelected ? 'white' : '#374151',
      '&:active': {
        backgroundColor: '#5882C1',
      },
    }),
    singleValue: base => ({
      ...base,
      fontSize: '14px',
      fontWeight: 'normal',
      color: disabled ? 'rgba(0, 0, 0, 0.38)' : '#212529',
    }),
    placeholder: base => ({
      ...base,
      fontSize: '14px',
      fontWeight: 'normal',
      color: '#b1b1b1',
    }),
  };

  const selectDefaultProps = {
    id: `${label}-address-select-widget`,
    value,
    options: options?.enumOptions || [],
    onChange: option => option?.value && onChange(option.value),
    placeholder: null,
    isDisabled: disabled,
    styles: selectStyles,
    components: {
      Input: inputProps => <components.Input {...inputProps} data-cy={`${label}-address-select-widget`} />,
      IndicatorSeparator: () => null,
    },
    noOptionsMessage: () => intl.formatMessage({ id: 'NO_OPTIONS_AVAILABLE_MESSAGE' }),
  };

  return (
    <div className="w-full">
      <label className="inline-block text-[12px] font-bold text-[#212529] font-['Open_Sans'] mt-[0.3vh] mb-1 tracking-tight">
        {intl.formatMessage({ id: label })}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>

      {options?.creatable ? (
        <CreatableSelect
          {...selectDefaultProps}
          onCreateOption={onCreateOption}
          formatCreateLabel={userInput => `${intl.formatMessage({ id: 'INSERT' })}: ${userInput}`}
        />
      ) : (
        <Select {...selectDefaultProps} />
      )}
    </div>
  );
};

AddressSelectWidget.propTypes = {
  disabled: PropTypes.bool,
  label: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onCreateOption: PropTypes.func,
  options: PropTypes.object.isRequired,
  required: PropTypes.bool.isRequired,
  value: PropTypes.object,
};

AddressSelectWidget.defaultProps = {
  disabled: false,
  onCreateOption: () => {},
  value: null,
};

export default AddressSelectWidget;
